import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { MapPin, Phone, Key, Copy, PencilSimple, Trash, ClockCounterClockwise, Briefcase, ClipboardText } from "@phosphor-icons/react";
import PropertyHistory from "../PropertyHistory";
import { toast } from "sonner";

export const propertyAddress = (p) => [p.address_line, p.town_city, p.county, p.postcode].filter(Boolean).join(", ");

/** Deep links that open the New Job / New Request dialogs with this property's client + address pre-filled. */
export const raiseLinks = (p) => ({
  job: `/jobs?new=1&for_client=${encodeURIComponent(p.client_id)}&for_property=${encodeURIComponent(p.id)}`,
  request: `/job-requests?new=1&for_client=${encodeURIComponent(p.client_id)}&for_property=${encodeURIComponent(p.id)}`,
});

/** Compact portfolio card used on the Properties tab. onEdit(p) / onRemove(p) are wired by the parent box. */
export default function PropertyCard({ property: p, onEdit, onRemove }) {
  const navigate = useNavigate();
  const [showHistory, setShowHistory] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(propertyAddress(p)); toast.success("Address copied"); };
  const work = `${p.request_count || 0} requests · ${p.job_count || 0} jobs${p.visit_count ? ` · ${p.visit_count} visits` : ""} · ${p.emergency_count || 0} emergencies`;

  return (
    <div className={`group border border-border rounded-md bg-card p-3 flex flex-col gap-1.5 transition-colors hover:border-primary/40 ${showHistory ? "sm:col-span-2 xl:col-span-3" : ""}`} data-testid={`property-card-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate" data-testid={`property-card-name-${p.id}`}>{p.name}</p>
          {p.property_type && <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{p.property_type}</p>}
        </div>
        <div className="flex items-center shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Copy address" onClick={copy} data-testid={`property-card-copy-${p.id}`}><Copy size={12} /></Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Edit" onClick={() => onEdit(p)} data-testid={`property-card-edit-${p.id}`}><PencilSimple size={12} /></Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-600" title="Remove" onClick={() => onRemove(p)} data-testid={`property-card-remove-${p.id}`}><Trash size={12} /></Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground flex items-start gap-1" data-testid={`property-card-address-${p.id}`}><MapPin size={13} className="shrink-0 mt-0.5 text-primary" /> {propertyAddress(p)}</p>
      {(p.site_contact_name || p.site_contact_phone) && (
        <p className="text-xs flex items-center gap-1.5 flex-wrap">
          {p.site_contact_name && <span className="font-medium">{p.site_contact_name}</span>}
          {p.site_contact_phone && <a href={`tel:${p.site_contact_phone}`} className="inline-flex items-center gap-1 text-primary hover:underline"><Phone size={11} /> {p.site_contact_phone}</a>}
        </p>
      )}
      {p.access_notes && (
        <p className="text-[11px] text-amber-800 dark:text-amber-300 flex items-start gap-1 line-clamp-2" title={p.access_notes}><Key size={11} className="shrink-0 mt-0.5" /> {p.access_notes}</p>
      )}
      <div className="mt-auto pt-1 flex items-center justify-between gap-2">
        <p className="text-[10px] text-muted-foreground" data-testid={`property-card-work-${p.id}`}>{work}</p>
        <button type="button" onClick={() => setShowHistory(h => !h)} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1" data-testid={`property-card-history-btn-${p.id}`}>
          <ClockCounterClockwise size={12} /> {showHistory ? "Hide history" : "History"}
        </button>
      </div>
      <div className="flex items-center gap-1.5 pt-1 border-t border-border/60">
        <Button size="sm" className="h-6 text-[11px] px-2" onClick={() => navigate(raiseLinks(p).job)} title="Open the New Job form with this client and address filled in" data-testid={`property-card-new-job-${p.id}`}><Briefcase size={11} className="mr-1" /> New job</Button>
        <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => navigate(raiseLinks(p).request)} title="Open the New Job Request form with this client and address filled in" data-testid={`property-card-new-request-${p.id}`}><ClipboardText size={11} className="mr-1" /> New request</Button>
        {p.latitude == null && <span className="ml-auto text-[10px] text-muted-foreground italic" title="No map location yet — it is placed when you open the map view" data-testid={`property-card-unpinned-${p.id}`}>Not on map</span>}
      </div>
      {showHistory && (
        <div className="border-t border-border pt-2 mt-1">
          <PropertyHistory endpoint={`/clients/${p.client_id}/properties/${p.id}/history`} testid={`property-card-history-${p.id}`} />
        </div>
      )}
    </div>
  );
}
